"use client";

import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
} from "react";
import { useCheckoutProduct } from "../hooks/use-checkout-product";
import { CheckoutPanel } from "../checkout-panel";
import { CheckoutFooter } from "../checkout-footer";
import { CheckoutSummaryCard } from "../checkout-summary-card";
import { ProductSelectionStep } from "../product-selection-step";
import { UserFormStep } from "../user-form-step";
import { PersonalDetailsStep } from "../personal-details-step";
import { BasketChoiceStep, type BasketChoiceNotice } from "../basket-choice-step";
import { Label } from "../../label";
import { Input } from "../../input";
import { validateGuest } from "../validation";
import type { UserFormAnswer } from "../utils/types";

export type CheckoutProductProps = {
  // Data
  product: any;
  postages?: any[];
  postageOptions?: Array<{
    id: string;
    name: string;
    description?: string;
    price: number;
    estimatedDays?: number;
  }>;
  user?: any;
  discount?: any;
  isGiftDefault?: boolean;

  // Callbacks
  onConfirmFreeBooking?: (data: any) => Promise<void>;
  onValidateDiscount?: (code: string, email: string) => Promise<any>;
  onSuccess?: (purchase: any) => void;
  onError?: (error: Error | string) => void;
  onClose?: () => void;
  onStepChange?: (step: string) => void;

  // Config
  currency?: string;
  timezone?: string;
  cancellationPolicyUrl?: string;
  /** Terms settings for dialog display */
  termsSettings?: import("../utils/types").TermsSettings;
  /** Custom terms for dialog display */
  customTerms?: import("../utils/types").CustomTerms;
  /** Callback for webcomponents to open terms dialog in external portal */
  onOpenTermsDialog?: (data: {
    termsSettings?: import("../utils/types").TermsSettings;
    customTerms?: import("../utils/types").CustomTerms;
    timezone?: string;
  }) => void;

  // School settings
  school?: {
    name: string;
    _id: string;
    isNewsletterSignupEnabled?: boolean;
    businessCountry?: string;
  };
  /** When true, footer is not sticky - flows after content (for iframe embeds) */
  isEmbedMode?: boolean;

  // Basket feature
  /** Current number of items in the basket */
  basketItemCount?: number;
  /** Callback when user clicks "Add to Basket" */
  onAddToBasket?: (itemData: {
    type: "product";
    productId: string;
    productName: string;
    quantity: number;
    guestDetails?: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    checkoutGuestsInformation?: Array<{
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      marketingOptIn?: boolean;
      mandatoryTickBox?: boolean;
    }>;
    price: number;
    currency: string;
    imageUrl?: string;
    selectedPostageId?: string;
    isGift?: boolean;
    /** Shaped to `BasketItem.giftingDetails` (the basket checkout page
     *  forwards it verbatim, and processBasketItem maps recipientName →
     *  `to` / senderName → `from` on the ProductPurchase). */
    giftingDetails?: {
      message: string;
      recipientName: string;
      senderName: string;
    };
    /** Answers to the product's booking questions (the userform step). */
    userInputs?: UserFormAnswer[];
  }) => void;
  onProceedToBasketCheckout: (itemData: any) => void;
  /** When true, skip the basket choice UI and auto-fire onProceedToCheckout on mount (single-item direct lane). */
  directCheckout?: boolean;
  /** Why the host refused the last basket choice; shown on the basket choice step. */
  basketConflict?: BasketChoiceNotice | null;
};

export function CheckoutProduct({
  product,
  postages = [],
  postageOptions: externalPostageOptions,
  user,
  discount: initialDiscount,
  isGiftDefault = false,
  onValidateDiscount,
  onClose,
  onStepChange,
  currency = "GBP",
  timezone,
  cancellationPolicyUrl = "/terms",
  termsSettings,
  customTerms,
  onOpenTermsDialog,
  school,
  isEmbedMode = false,
  basketItemCount = 0,
  onAddToBasket,
  onProceedToBasketCheckout,
  directCheckout = false,
  basketConflict = null,
}: CheckoutProductProps) {
  const checkout = useCheckoutProduct({
    product,
    postages,
    user,
    discount: initialDiscount,
    school,
    onValidateDiscount,
  });

  // Guest validation state
  const [guestTouched, setGuestTouched] = useState<
    Array<Partial<Record<string, boolean>>>
  >([]);

  // Compute guest validation errors
  const guestErrors = useMemo(() => {
    return checkout.personalDetails.guests.map((guest: any, index: number) =>
      validateGuest(
        {
          firstName: guest.firstName || "",
          lastName: guest.lastName || "",
          email: guest.email || "",
          phoneNumber: guest.phoneNumber || "",
        },
        {
          isFirstGuest: index === 0,
          requirePhone: false, // Product checkout doesn't require phone
        },
      ),
    );
  }, [checkout.personalDetails.guests]);

  // Handle guest field blur for validation
  const handleGuestBlur = useCallback((guestIndex: number, field: string) => {
    setGuestTouched((prev) => {
      const newTouched = [...prev];
      if (!newTouched[guestIndex]) {
        newTouched[guestIndex] = {};
      }
      newTouched[guestIndex] = {
        ...newTouched[guestIndex],
        [field]: true,
      };
      return newTouched;
    });
  }, []);

  // Map postages for ProductSelectionStep - use external if provided
  const postageOptions =
    externalPostageOptions ||
    postages.map((p: any) => ({
      id: p._id || p.id,
      name: p.name,
      price: p.price ?? 0,
      description: p.description,
    }));

  // Call step change callback for scroll-to-top behavior
  useEffect(() => {
    onStepChange?.(checkout.step);
  }, [checkout.step, onStepChange]);

  // Build ticket items for summary
  const ticketItems = [
    {
      name: product.title || product.name,
      price: product.price,
      count: checkout.quantity,
    },
  ];

  if (checkout.selectedPostage) {
    ticketItems.push({
      name: `Postage: ${checkout.selectedPostage.name}`,
      price: checkout.selectedPostage.price,
      // Per unit, matching what the hook totals and what the server charges.
      count: checkout.quantity,
    });
  }

  if (checkout.isGift && product.gifting?.giftingFee) {
    ticketItems.push({
      name: "Gift wrapping",
      price: product.gifting.giftingFee,
      count: 1,
    });
  }

  const handleAddToBasket = useCallback(() => {
    if (!onAddToBasket) return;
    onAddToBasket({
      type: "product",
      productId: product._id || product.id,
      productName: product.title || product.name,
      quantity: checkout.quantity,
      price: checkout.total,
      currency,
      imageUrl: product.images?.[0]?.url,
      selectedPostageId: checkout.selectedPostageId,
      isGift: checkout.isGift,
      // Only when gifting is actually on: the hook keeps the fields populated
      // after the buyer un-ticks the gift option, so sending them
      // unconditionally would gift-wrap a non-gift order.
      giftingDetails: checkout.isGift
        ? {
            message: checkout.giftingDetails.message,
            recipientName: checkout.giftingDetails.to,
            senderName: checkout.giftingDetails.from,
          }
        : undefined,
      guestDetails: {
        firstName: checkout.personalDetails.guests[0]?.firstName || "",
        lastName: checkout.personalDetails.guests[0]?.lastName || "",
        email: checkout.personalDetails.guests[0]?.email || "",
        phone: checkout.personalDetails.guests[0]?.phoneNumber || "",
      },
      checkoutGuestsInformation: checkout.personalDetails.guests.map((g: any) => ({
        firstName: g.firstName || "",
        lastName: g.lastName || "",
        email: g.email || "",
        phoneNumber: g.phoneNumber || "",
        marketingOptIn: g.marketingOptIn || false,
        mandatoryTickBox: g.mandatoryTickBox,
      })),
      // The booking-question answers. Dropped here until Oct 2026, so basket
      // product purchases lost every answer the buyer gave.
      userInputs: checkout.userFormValues,
    });
  }, [onAddToBasket, product, checkout.quantity, checkout.total, checkout.selectedPostageId, checkout.isGift, checkout.giftingDetails, checkout.personalDetails, checkout.userFormValues, currency]);

  const handleProceedToBasketCheckout = useCallback(() => {
    onProceedToBasketCheckout({
      type: "product",
      productId: product._id || product.id,
      productName: product.title || product.name,
      quantity: checkout.quantity,
      price: checkout.total,
      currency,
      imageUrl: product.images?.[0]?.url,
      selectedPostageId: checkout.selectedPostageId,
      isGift: checkout.isGift,
      // See handleAddToBasket.
      giftingDetails: checkout.isGift
        ? {
            message: checkout.giftingDetails.message,
            recipientName: checkout.giftingDetails.to,
            senderName: checkout.giftingDetails.from,
          }
        : undefined,
      guestDetails: {
        firstName: checkout.personalDetails.guests[0]?.firstName || "",
        lastName: checkout.personalDetails.guests[0]?.lastName || "",
        email: checkout.personalDetails.guests[0]?.email || "",
        phone: checkout.personalDetails.guests[0]?.phoneNumber || "",
      },
      checkoutGuestsInformation: checkout.personalDetails.guests.map((g: any) => ({
        firstName: g.firstName || "",
        lastName: g.lastName || "",
        email: g.email || "",
        phoneNumber: g.phoneNumber || "",
        marketingOptIn: g.marketingOptIn || false,
        mandatoryTickBox: g.mandatoryTickBox,
      })),
      // See handleAddToBasket.
      userInputs: checkout.userFormValues,
    });
  }, [onProceedToBasketCheckout, product, checkout.quantity, checkout.total, checkout.selectedPostageId, checkout.isGift, checkout.giftingDetails, checkout.personalDetails, checkout.userFormValues, currency]);

  return (
    <>
      <CheckoutPanel>
        <CheckoutPanel.Header
          title={product.title || product.name}
          subtitle="Product Purchase"
          onClose={onClose}
        />

        <CheckoutPanel.Content isEmbedMode={isEmbedMode}>
          <CheckoutPanel.Main>
            {/* Step Navigation */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              {checkout.visibleSteps.map((stepName, index) => (
                <React.Fragment key={stepName}>
                  {index > 0 && (
                    <span className="text-muted-foreground/70">/</span>
                  )}
                  <button
                    onClick={() =>
                      index < checkout.currentStepIndex &&
                      checkout.goToStep(stepName)
                    }
                    disabled={index > checkout.currentStepIndex}
                    className={`capitalize ${
                      index === checkout.currentStepIndex
                        ? "text-primary font-medium"
                        : index < checkout.currentStepIndex
                          ? "text-muted-foreground hover:text-primary cursor-pointer"
                          : "text-muted-foreground/70"
                    }`}
                  >
                    {stepName}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Product Selection Step */}
            {checkout.step === "product" && (
              <ProductSelectionStep
                productName={product.title || product.name}
                productPrice={product.price ?? 0}
                productStock={product.stock ?? 99}
                selectedQuantity={checkout.quantity ?? 1}
                onQuantityChange={checkout.setQuantity}
                postageOptions={postageOptions}
                selectedPostageId={checkout.selectedPostageId}
                onPostageChange={checkout.setSelectedPostageId}
                giftingEnabled={!!product.gifting?.showGifting}
                isGift={checkout.isGift}
                onGiftChange={checkout.setIsGift}
                giftingFee={product.gifting?.giftingFee ?? 0}
                giftingDetails={checkout.giftingDetails}
                onGiftingDetailsChange={checkout.updateGiftingDetails}
                doTeacherWraps={!!product.gifting?.doTeacherWraps}
                currency={currency}
              />
            )}

            {/* User Form Step */}
            {checkout.step === "userform" && (
              <UserFormStep
                questions={product.userQuestionForm?.questions || []}
                totalGuests={1}
                values={checkout.userFormValues}
                onChange={checkout.setUserFormValues}
                onValidationChange={checkout.setIsUserFormValid}
                mode={product.userQuestionForm?.mode || "all"}
              />
            )}

            {/* Personal Details Step with Delivery Address */}
            {checkout.step === "personal" && (
              <div className="space-y-6">
                <PersonalDetailsStep
                  values={{
                    firstName:
                      checkout.personalDetails.guests[0]?.firstName || "",
                    lastName:
                      checkout.personalDetails.guests[0]?.lastName || "",
                    email: checkout.personalDetails.guests[0]?.email || "",
                    phone:
                      checkout.personalDetails.guests[0]?.phoneNumber || "",
                    marketingOptIn:
                      checkout.personalDetails.guests[0]?.marketingOptIn ||
                      false,
                  }}
                  errors={{
                    firstName: guestErrors[0]?.firstName,
                    lastName: guestErrors[0]?.lastName,
                    email: guestErrors[0]?.email,
                    phone: guestErrors[0]?.phoneNumber,
                  }}
                  touched={{
                    firstName: guestTouched[0]?.firstName,
                    lastName: guestTouched[0]?.lastName,
                    email: guestTouched[0]?.email,
                    phone: guestTouched[0]?.phoneNumber,
                  }}
                  onChange={(field, value) => {
                    const fieldMap: Record<string, string> = {
                      firstName: "firstName",
                      lastName: "lastName",
                      email: "email",
                      phone: "phoneNumber",
                      marketingOptIn: "marketingOptIn",
                    };
                    checkout.updatePersonalDetails(
                      0,
                      fieldMap[field] as any,
                      value,
                    );
                  }}
                  onBlur={(field) => {
                    const fieldMap: Record<string, string> = {
                      firstName: "firstName",
                      lastName: "lastName",
                      email: "email",
                      phone: "phoneNumber",
                    };
                    handleGuestBlur(0, fieldMap[field] || field);
                  }}
                  schoolID={school?._id || ""}
                  businessCountry={school?.businessCountry}
                />

                {/* Delivery Address */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-semibold text-lg">Delivery Address</h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="delivery-firstName">First name *</Label>
                      <Input
                        id="delivery-firstName"
                        value={
                          checkout.personalDetails.deliveryAddress?.firstName ||
                          ""
                        }
                        onChange={(e) =>
                          checkout.updateDeliveryAddress(
                            "firstName",
                            e.target.value,
                          )
                        }
                        placeholder="First name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="delivery-lastName">Last name *</Label>
                      <Input
                        id="delivery-lastName"
                        value={
                          checkout.personalDetails.deliveryAddress?.lastName ||
                          ""
                        }
                        onChange={(e) =>
                          checkout.updateDeliveryAddress(
                            "lastName",
                            e.target.value,
                          )
                        }
                        placeholder="Last name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="addressLine1">Address line 1 *</Label>
                    <Input
                      id="addressLine1"
                      value={
                        checkout.personalDetails.deliveryAddress
                          ?.addressLine1 || ""
                      }
                      onChange={(e) =>
                        checkout.updateDeliveryAddress(
                          "addressLine1",
                          e.target.value,
                        )
                      }
                      placeholder="Street address"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="addressLine2">Address line 2</Label>
                    <Input
                      id="addressLine2"
                      value={
                        checkout.personalDetails.deliveryAddress
                          ?.addressLine2 || ""
                      }
                      onChange={(e) =>
                        checkout.updateDeliveryAddress(
                          "addressLine2",
                          e.target.value,
                        )
                      }
                      placeholder="Apartment, suite, etc. (optional)"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={
                          checkout.personalDetails.deliveryAddress?.city || ""
                        }
                        onChange={(e) =>
                          checkout.updateDeliveryAddress("city", e.target.value)
                        }
                        placeholder="City"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="postCode">Post code *</Label>
                      <Input
                        id="postCode"
                        value={
                          checkout.personalDetails.deliveryAddress?.postCode ||
                          ""
                        }
                        onChange={(e) =>
                          checkout.updateDeliveryAddress(
                            "postCode",
                            e.target.value,
                          )
                        }
                        placeholder="Post code"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="region">Region / State</Label>
                      <Input
                        id="region"
                        value={
                          checkout.personalDetails.deliveryAddress?.region || ""
                        }
                        onChange={(e) =>
                          checkout.updateDeliveryAddress(
                            "region",
                            e.target.value,
                          )
                        }
                        placeholder="Region or state (optional)"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        value={
                          checkout.personalDetails.deliveryAddress?.country ||
                          ""
                        }
                        onChange={(e) =>
                          checkout.updateDeliveryAddress(
                            "country",
                            e.target.value,
                          )
                        }
                        placeholder="Country"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contactNumber">Contact number</Label>
                    <Input
                      id="contactNumber"
                      type="tel"
                      value={
                        checkout.personalDetails.deliveryAddress
                          ?.contactNumber || ""
                      }
                      onChange={(e) =>
                        checkout.updateDeliveryAddress(
                          "contactNumber",
                          e.target.value,
                        )
                      }
                      placeholder="Phone number for delivery"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="additionalNotes">
                      Additional notes (optional)
                    </Label>
                    <Input
                      id="additionalNotes"
                      value={
                        checkout.personalDetails.deliveryAddress
                          ?.additionalNotes || ""
                      }
                      onChange={(e) =>
                        checkout.updateDeliveryAddress(
                          "additionalNotes",
                          e.target.value,
                        )
                      }
                      placeholder="Delivery instructions, etc."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Basket Choice Step */}
            {checkout.step === "basketChoice" && (
              <BasketChoiceStep
                itemName={product.title || product.name}
                total={checkout.total}
                currency={currency}
                basketItemCount={basketItemCount}
                onAddToBasket={handleAddToBasket}
                onProceedToCheckout={handleProceedToBasketCheckout}
                directCheckout={directCheckout}
                basketConflict={basketConflict}
              />
            )}
          </CheckoutPanel.Main>

          <CheckoutPanel.Sidebar>
            <CheckoutSummaryCard
              title={product.title || product.name}
              thumbnail={product.images?.[0]?.url}
              items={ticketItems}
              total={checkout.total}
              currency={currency}
              discount={checkout.discountAmount}
              cancellationPolicyUrl={cancellationPolicyUrl}
              termsSettings={termsSettings}
              customTerms={customTerms}
              timezone={timezone}
              onOpenTermsDialog={onOpenTermsDialog}
            />
          </CheckoutPanel.Sidebar>
        </CheckoutPanel.Content>
      </CheckoutPanel>

      {/* Footer */}
      <CheckoutFooter
        total={checkout.total}
        currency={currency}
        showBack={!checkout.isFirstStep}
        onBack={checkout.goToPreviousStep}
        showCancel={!!onClose}
        onCancel={onClose}
        showContinue={checkout.step !== "basketChoice"}
        onContinue={checkout.goToNextStep}
        continueText="Continue"
        continueDisabled={!checkout.canContinue()}
        isEmbedMode={isEmbedMode}
      />
    </>
  );
}
